import {
  type DealApi,
  type DealFormData,
  type DealFormDataWithTargeting,
  type DealParticipant,
  type DealState,
  dealFormField,
  type PmpDealType,
  pmpDealType,
  rboConfigField,
} from '@openx/types/deal';
import { type OrganizationType, organizationType } from '@openx/types/organization';
import type { PackageFormState } from '@openx/types/package';
import type { TargetingState } from '@openx/types/targeting/targeting';
import { AMAZON_DEMAND_PARTNER_ID } from '@openx/utils/services/config';
import type { TFunction } from 'i18next';
import { dealDefaultInitialValues, initDealParticipants, userDefinedIdFieldsInitialValues } from './constants';
import { mapDealToTemplateDeal } from './mapDealToTemplateDeal';
import { parseDealDate } from './parseDealDate';
import { parseThirdPartyFeesConfig } from './parseThirdPartyFeesConfig';

type GetInitialDealFormValuesParameters = {
  // when navigating back from Package form, the deal has the `DealFormData` type
  deal?: DealState | DealFormDataWithTargeting;
  defaultAccountUid: string | null;
  hasGlobalDeals?: boolean;
  isAllowedToPlatformShareWrite?: boolean;
  isOa?: boolean;
  isBef?: boolean;
  organizationType?: OrganizationType;
  packageUid?: string;
  useSharedTargeting?: boolean;
};

export const organizationTypeToSourcedMap = {
  [organizationType.TechPartner]: 'Tech Partner Sourced',
  [organizationType.Buyer]: 'Buyer Sourced',
  [organizationType.Publisher]: 'Publisher',
  [organizationType.BuyerDev]: 'OpenX - Buyer Dev Sourced',
} as const;

/**
 * Convert API pdp_config values to form representation.
 * min_win_rate and threshold arrive as decimal strings (e.g. "0.400") from the API
 * but are stored as integer percentage strings (e.g. "40") in the form.
 */
function toFormPdpConfig(pdpConfig: DealState['pdp_config'] | undefined): DealFormData['pdp_config'] {
  if (!pdpConfig) {
    return null;
  }

  return {
    ...pdpConfig,
    ...(pdpConfig.min_win_rate !== null &&
      pdpConfig.min_win_rate !== undefined && {
        min_win_rate: String(Math.round(parseFloat(pdpConfig.min_win_rate) * 100)),
      }),
    ...(pdpConfig.threshold !== null &&
      pdpConfig.threshold !== undefined && {
        threshold: String(Math.round(parseFloat(pdpConfig.threshold) * 100)),
      }),
  };
}

export const getInitialDealFormValues = ({
  deal,
  defaultAccountUid,
  hasGlobalDeals,
  isAllowedToPlatformShareWrite,
  isOa,
  isBef,
  organizationType,
  packageUid,
  useSharedTargeting,
}: GetInitialDealFormValuesParameters): DealFormData => {
  const isPub = !isOa && !isBef;

  if (deal) {
    const parsedEndDate = parseDealDate(deal.end_date);
    const parsedStartDate = parseDealDate(deal.start_date);

    const skipPlatformShareParsing = useSharedTargeting && !isAllowedToPlatformShareWrite;
    const third_party_fees_config = parseThirdPartyFeesConfig(deal.third_party_fees_config, skipPlatformShareParsing);
    const third_party_fees_config_enabled = !!deal.third_party_fees_config?.length;

    if (useSharedTargeting) {
      return {
        ...deal,
        ...userDefinedIdFieldsInitialValues,
        end_date: parsedEndDate,
        start_date: parsedStartDate,
        third_party_fees_config,
        third_party_fees_config_enabled,
        segment_partner_revenue_method: deal.segment_partner_revenue_method ?? null,
        [dealFormField.PDP_CONFIG]: toFormPdpConfig(deal[dealFormField.PDP_CONFIG]),
        [dealFormField.AMAZON_CONFIG]: deal[dealFormField.AMAZON_CONFIG] ?? null,
        ...(isPub && { [dealFormField.BUYER_ACCOUNT_ID]: deal[dealFormField.BUYER_ACCOUNT_ID] ?? null }),
      };
    }

    return {
      ...deal,
      end_date: parsedEndDate,
      start_date: parsedStartDate,
      third_party_fees_config,
      third_party_fees_config_enabled,
      segment_partner_revenue_method: deal.segment_partner_revenue_method ?? null,
      [dealFormField.PDP_CONFIG]: toFormPdpConfig(deal[dealFormField.PDP_CONFIG]),
      [dealFormField.AMAZON_CONFIG]: deal[dealFormField.AMAZON_CONFIG] ?? null,
      ...(isPub && { [dealFormField.BUYER_ACCOUNT_ID]: deal[dealFormField.BUYER_ACCOUNT_ID] ?? null }),
    };
  }

  return {
    ...dealDefaultInitialValues,
    [dealFormField.ACCOUNT_UID]: defaultAccountUid || null,
    [dealFormField.PACKAGE_UID]: packageUid || null,
    [dealFormField.DEAL_SOURCE]: isOa
      ? organizationType
        ? organizationTypeToSourcedMap[organizationType]
        : null
      : dealDefaultInitialValues[dealFormField.DEAL_SOURCE],
    ...(hasGlobalDeals && {
      [dealFormField.PMP_DEAL_TYPE]: pmpDealType.WithinOpenAuction,
    }),
    ...userDefinedIdFieldsInitialValues,
    ...(isPub && { [dealFormField.BUYER_ACCOUNT_ID]: null }),
  };
};

export const modifyDealParticipants = (
  participants: DealParticipant[],
  isDV360DemandPartner: boolean,
  enableDV360InventoryPackage?: boolean
): DealParticipant[] => {
  return participants.map(participant => {
    const shouldAddBuyerId =
      Object.keys(participant.buyer_ids || []).length === 1 && isDV360DemandPartner && !enableDV360InventoryPackage;

    const buyerIds = Array.isArray(participant.buyer_ids)
      ? participant.buyer_ids
      : Object.keys(participant.buyer_ids || {});

    const buyerId = Array.isArray(participant.buyer_ids)
      ? participant.buyer_ids[0]
      : Object.keys(participant.buyer_ids).toString();

    return {
      ...participant,
      ...(shouldAddBuyerId && { buyer_id: buyerId }),
      brand_ids: Array.isArray(participant.brand_ids)
        ? participant.brand_ids
        : Object.keys(participant.brand_ids || {}),
      buyer_ids: buyerIds.filter(id => id !== participant.main_buyer_id),
    };
  });
};

export const getUseAsTemplateDeal = (
  deal: DealApi | DealState,
  demandPartnerIds: Record<string, string>,
  canCreateAmazonMarketplaceDeal = false
): DealState => {
  const templateDeal = mapDealToTemplateDeal(deal);
  const isSourceSyncedWithAmazon = deal.synchronized_with?.amazon_sync != null;
  const isDealParticipantsInitializedAndNotEmpty = templateDeal.deal_participants?.[0];
  const { dv360DemandPartnerId } = demandPartnerIds;
  const isDV360DemandPartner = templateDeal.deal_participants.some(
    ({ demand_partner }) => demand_partner === dv360DemandPartnerId
  );

  const dealParticipants = modifyDealParticipants(
    templateDeal.deal_participants,
    isDV360DemandPartner,
    templateDeal.enable_dv360_inventory_package
  );

  // Amazon marketplace is a phantom field derived from empty seats; enable it out of the box only for
  // users allowed to create one, and drop any inherited flag for everyone else.
  const dealParticipantsWithMarketplace = dealParticipants.map(participant =>
    participant.demand_partner === AMAZON_DEMAND_PARTNER_ID
      ? {
          ...participant,
          amazon_sync_marketplace_enabled:
            canCreateAmazonMarketplaceDeal && isSourceSyncedWithAmazon && !participant.buyer_ids?.length,
        }
      : participant
  );

  return {
    ...templateDeal,
    deal_participants: isDealParticipantsInitializedAndNotEmpty
      ? dealParticipantsWithMarketplace
      : [{ ...initDealParticipants }],
    use_market_floor: templateDeal.use_market_floor !== null,
  };
};

export const convertTargetingToPackage = (targeting: TargetingState): PackageFormState => ({
  account_uid: '',
  domains: '',
  locked_internal: false,
  logo: '',
  name: '',
  notes: '',
  private_market: {
    default_discounted_cpm: '',
    default_rate_card_cpm: '',
    discoverable: false,
  },
  rate_card_cpm: '',
  targeting: targeting,
  urls: [],
});

export const getDealsLabels = (t: TFunction) => ({
  account_executive: t('Account Executive'),
  account_manager: t('Account Manager'),
  account_uid: t('Select Account for Deal'),
  agency_salesforce_id: t('Agency'),
  brand_salesforce_id: t('Brand'),
  currency: t('Currency'),
  deal_id: t('Deal ID'),
  deal_id_radio_generated: t('Generated by OpenX with prefix: '),
  deal_price: t('Deal Price'),
  deal_source: t('Deal Source'),
  end_date: t('End Date'),
  expected_ad_category: t('Expected Sensitive Category'),
  is_pacing_enabled: t('Enable Pacing'),
  lifetime_impression_goal: t('Impression Goal'),
  monetizing_budget: t('Budget'),
  monetizing_impression_goal: t('Impression Goal'),
  monetizing_region: t('Region'),
  name: t('Deal Name'),
  open_auction_access: t('Set Open Auction Access for Participants'),
  package_uid: t('Package'),
  partner_account_uids: t('Partner'),
  pmp_deal_type: t('Deal Type'),
  publisher_name: t('Publisher'),
  rbo_config: t('Enable Engagement Optimization'),
  start_date: t('Start Date'),
  status: t('Status'),
  use_market_floor: t('Use max of deal price or Exchange floors as floor'),
  buyer_account_id: t('OXS Buyer'),
});

export const getRboLabels = (t: TFunction) => ({
  [rboConfigField.HOURLY_TARGET]: t('Hourly Request Goal'),
  [rboConfigField.DROP_PERCENT]: t('% Traffic Drop'),
  [rboConfigField.CHANNEL]: t('Model Type'),
  [rboConfigField.MODEL_GOAL]: t('Model Goal'),
  [rboConfigField.TARGET]: t('Target'),
  [rboConfigField.TACTIC]: t('Tactic'),
  [rboConfigField.THRESHOLD]: t('Threshold'),
  [rboConfigField.CUSTOM_SCORE_NAME]: t('Custom Score Name'),
});

export const getRboPlaceholders = (t: TFunction) => ({
  [rboConfigField.HOURLY_TARGET]: t('Enter Hourly Request Goal e.g. 1000'),
  [rboConfigField.DROP_PERCENT]: t('Enter % Traffic Drop e.g. 10'),
  [rboConfigField.CHANNEL]: t('[ Select or Enter Model Type ]'),
  [rboConfigField.MODEL_GOAL]: t('[ Select or Enter Model Goal ]'),
  [rboConfigField.TARGET]: t('[ Select, Search or Enter Target ]'),
  [rboConfigField.TACTIC]: t('[ Select or Enter Tactic ]'),
  [rboConfigField.THRESHOLD]: t('Enter Threshold Override Value'),
  [rboConfigField.CUSTOM_SCORE_NAME]: t('Enter Custom Score Name Value'),
});

export const isChangingDealTypeToAuction = (currentState: DealState | undefined, formValues: DealFormData) => {
  if (!currentState) {
    return true;
  }

  const auctionDealTypes: PmpDealType[] = [pmpDealType.PrivateAuction, pmpDealType.WithinOpenAuction];
  const isCurrentDealTypeInAuction = auctionDealTypes.includes(currentState.pmp_deal_type);

  const isFormDealTypeInAuction = formValues.pmp_deal_type && auctionDealTypes.includes(formValues.pmp_deal_type);

  return !isCurrentDealTypeInAuction && isFormDealTypeInAuction;
};

export const isChangingDealTypeFromWithinOpenAudience = (
  currentState: DealState | undefined,
  formValues: DealFormData
) => {
  if (!currentState) {
    return false;
  }

  const isCurrentDealTypeWithinOpenAuction = currentState.pmp_deal_type === pmpDealType.WithinOpenAuction;
  const isFormDealTypeWithinOpenAuction = formValues.pmp_deal_type === pmpDealType.WithinOpenAuction;

  return isCurrentDealTypeWithinOpenAuction && !isFormDealTypeWithinOpenAuction;
};
